import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { useHistory, useLocation } from "react-router-dom";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

/**
 * OnboardingTour
 *
 * A first-run product tour for NEW Whapiz tenants. Walks the user through:
 *   1. Connect WhatsApp Business API (Meta Cloud)
 *   2. Create their first Queue
 *   3. Choose a bot type (Simple menu vs AI assistant vs Skip)
 *   4. Invite their team
 *
 * IMPORTANT — multi-tenancy isolation:
 *   - GoDeal (companyId === 1) is HARD-EXCLUDED. Raviv runs that account
 *     manually and the tour copy is wrong for travel-specific flows. See
 *     .cursor/rules/godeal-multi-tenancy-isolation.mdc.
 *   - Existing tenants who signed up before this feature shipped are also
 *     excluded (account older than 7 days) so they don't suddenly get a
 *     tour shoved in their face one morning.
 *
 * Persistence: tour completion is stored in localStorage AND optionally
 * synced to the backend (`Setting.onboardingTourCompleted`) for cross-device
 * persistence. localStorage is the source of truth for "skip me on this
 * device", the backend setting is what stops the tour from popping up when
 * the user logs in from a fresh browser after they've already completed it.
 */

const GODEAL_COMPANY_ID = "1";
// Tour-state keys are scoped per tenant so they don't leak across accounts
// when an admin (or QA / support) signs into multiple tenants from the same
// browser. A legacy GoDeal admin who already finished the tour on companyId=1
// would otherwise inherit the `completed=true` flag for every new tenant they
// log into next — making the tour silently never appear.
const STORAGE_KEY_PREFIX = "whapiz-tour-completed";
const STORAGE_STEP_PREFIX = "whapiz-tour-step";
const tourKey = (companyId) => `${STORAGE_KEY_PREFIX}:${companyId || "anon"}`;
const stepKey = (companyId) => `${STORAGE_STEP_PREFIX}:${companyId || "anon"}`;
// Manual-trigger flag lives in sessionStorage so it survives the
// `history.push(...)` calls between steps (which strip the query
// string), but does NOT leak into the next browser session.
const STORAGE_MANUAL_KEY = "whapiz-tour-manual";
const NEW_USER_WINDOW_DAYS = 14;

/**
 * True when at least one of the supplied timestamps is within the
 * NEW_USER_WINDOW. We accept multiple inputs because:
 *   - `user.createdAt` is the most accurate signal but it was missing
 *     from the auth payload until 2026-04-29.
 *   - `company.createdAt` is a reliable fallback — if the *company* is
 *     fresh, the tour copy still makes sense even for an admin user
 *     that was reassigned from another tenant.
 */
const isFreshTenant = (...timestamps) => {
  for (const ts of timestamps) {
    if (!ts) continue;
    const ms = new Date(ts).getTime();
    if (Number.isNaN(ms)) continue;
    const ageDays = (Date.now() - ms) / 86400000;
    if (ageDays <= NEW_USER_WINDOW_DAYS) return true;
  }
  return false;
};

const hasManualQueryParam = () => {
  if (typeof window === "undefined") return false;
  return /[?&]startTour=1\b/.test(window.location.search);
};

const isDebugMode = () => {
  if (typeof window === "undefined") return false;
  if (/[?&]tourDebug=1\b/.test(window.location.search)) return true;
  try {
    return sessionStorage.getItem("whapiz-tour-debug") === "true";
  } catch (e) {
    return false;
  }
};

/**
 * On phones (<768px) the sidebar collapses into a Drawer that's only
 * visible after tapping the hamburger menu — so menu-* targets aren't
 * reachable via spotlight. We fall back to centered "body" tooltips
 * with a written instruction ("tap the menu → Connections") instead.
 */
const isMobileViewport = () => {
  if (typeof window === "undefined") return false;
  return window.innerWidth < 768;
};

const buildSteps = (t) => {
  const mobile = isMobileViewport();
  // Helper: pick a sidebar selector on desktop, or fall back to "body"
  // with placement="center" on mobile where the sidebar is hidden behind
  // the hamburger drawer.
  const menuStep = (selector, placement) =>
    mobile
      ? { target: "body", placement: "center" }
      : { target: selector, placement };

  return [
    {
      target: "body",
      placement: "center",
      disableBeacon: true,
      title: t("tour.welcome.title", "Welcome to Whapiz! 👋"),
      content: t(
        "tour.welcome.content",
        "Let's set up your business in 4 simple steps. It takes about 5 minutes."
      ),
    },
    {
      ...menuStep('[data-tour="menu-connections"]', "right"),
      disableBeacon: true,
      title: t("tour.connections.title", "Step 1 — Connect your WhatsApp number"),
      content: mobile
        ? t(
            "tour.connections.contentMobile",
            "Tap the ☰ menu in the top-left, then choose 'Connections'. That's where you connect your WhatsApp Business API number."
          )
        : t(
            "tour.connections.content",
            "Click here to open the Connections page where you'll add your WhatsApp Business API number."
          ),
    },
    {
      target: '[data-tour="connect-meta-business"]',
      placement: mobile ? "top" : "bottom",
      disableBeacon: true,
      title: t("tour.metaButton.title", "Click 'Connect WhatsApp Business API'"),
      content: t(
        "tour.metaButton.content",
        "Raviv's team will provide you with 3 values from Meta Business Manager: Phone Number ID, WABA ID, and an API Token. Paste them in the wizard's step 2 and you're done."
      ),
    },
    {
      ...menuStep('[data-tour="menu-queues"]', "right"),
      disableBeacon: true,
      title: t("tour.queues.title", "Step 2 — Create your first queue"),
      content: mobile
        ? t(
            "tour.queues.contentMobile",
            "Tap the ☰ menu, then 'Queues'. Queues organize incoming chats by department (Sales, Support, Billing…). Tap '+Add' to create your first one."
          )
        : t(
            "tour.queues.content",
            "Queues organize incoming chats by department or topic (Sales, Support, Billing…). Click here, then '+Add' to create your first one."
          ),
    },
    {
      target: "body",
      placement: "center",
      disableBeacon: true,
      title: t("tour.botChoice.title", "Step 3 — Choose how your bot replies"),
      content: t(
        "tour.botChoice.content",
        "You have two options:\n\n📋 SIMPLE MENU BOT — Customers see a numbered menu (1 - Sales, 2 - Support…). Easy to set up inside each Queue → Options tab.\n\n🤖 AI ASSISTANT — GPT-4 answers in natural language. Best for sales and complex questions. The next step opens the AI setup wizard for you.\n\nYou can skip this for now and decide later."
      ),
    },
    {
      // OR-selector: the AI wizard form (for fresh tenants who get
      // redirected from /ai-settings -> /ai-config-wizard) OR the
      // 'Enable AI' master toggle (for tenants who already finished
      // the wizard once). Joyride uses document.querySelector under
      // the hood, so the first matching element wins.
      target: '[data-tour="ai-wizard-form"], [data-tour="ai-toggle"]',
      placement: "top",
      disableBeacon: true,
      title: t("tour.aiSettings.title", "AI Assistant (optional)"),
      content: t(
        "tour.aiSettings.content",
        "Fill in the 6 short steps to teach the AI about your business: company info, tone of voice, knowledge sources, CRM integration, when to hand off to a human, and the greeting. The AI uses GPT-4 and answers in your customer's language. Skip this step if you'd rather use a simple menu bot."
      ),
    },
    {
      ...menuStep('[data-tour="menu-users"]', "right"),
      disableBeacon: true,
      title: t("tour.users.title", "Step 4 — Invite your team"),
      content: mobile
        ? t(
            "tour.users.contentMobile",
            "Tap the ☰ menu, then 'Users'. Add the agents who'll handle customer chats — each agent gets their own login and inbox."
          )
        : t(
            "tour.users.content",
            "Add the agents who will handle customer chats. Each agent gets their own login and inbox."
          ),
    },
    {
      target: "body",
      placement: "center",
      disableBeacon: true,
      title: t("tour.done.title", "🎉 You're all set!"),
      content: t(
        "tour.done.content",
        "Your customers can now message you and your team can reply from here. You can restart this tour anytime from your profile menu."
      ),
    },
  ];
};

/**
 * Maps a tour step index to the route the user should be on for that step.
 * If the user is on the wrong page when the step starts, we navigate them
 * there so the highlighted target is actually visible.
 *
 * Step 5 (AI) goes to /ai-config-wizard — fresh tenants without an AI
 * config get auto-redirected from /ai-settings to /ai-config-wizard, and
 * navigating directly avoids a redirect loop in the tour. If the wizard
 * is already done, /ai-config-wizard renders a "you're done" view and
 * the OR-selector picks up the 'Enable AI' toggle on /ai-settings if
 * the user navigated there manually.
 */
const STEP_ROUTES = {
  1: "/", // After welcome — go to home so menu is visible
  2: "/connections", // Click "Connect Meta Business API"
  3: "/queues",
  4: "/queues",
  5: "/ai-config-wizard",
  6: "/users",
};

const OnboardingTour = () => {
  const { user, loading: authLoading } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();

  // Per-tenant storage keys derived from the logged-in user. Computed lazily
  // so the initial render (before AuthContext resolves) doesn't read keys
  // for an "anon" tenant and then re-key on the next render.
  const tenantCompanyId = String(user?.companyId || "");
  const STORAGE_KEY = tourKey(tenantCompanyId);
  const STORAGE_STEP_KEY = stepKey(tenantCompanyId);

  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(() => {
    if (!tenantCompanyId) return 0;
    const saved = parseInt(
      localStorage.getItem(stepKey(tenantCompanyId)) || "0",
      10
    );
    return Number.isFinite(saved) && saved >= 0 ? saved : 0;
  });
  // Hard kill-switch flipped by finishTour. We CANNOT rely on
  // `setRun(false)` alone — joyride sometimes re-emits events during
  // unmount, the eligible memo depends on stale localStorage reads,
  // and React batches in ways that make derived state unreliable for
  // "tour is over". This boolean short-circuits the whole render path.
  const [tourClosed, setTourClosed] = useState(false);

  // We optimistically trust localStorage to skip a network round-trip on
  // the common path. The backend `/portal/tour/state` is consulted once on
  // mount as a fallback so a fresh browser sees the same state as the user
  // already had on another device.
  const [serverChecked, setServerChecked] = useState(false);

  const localCompleted = localStorage.getItem(STORAGE_KEY) === "true";

  // Manual trigger persists in sessionStorage. We CANNOT recompute it from
  // window.location.search on every render because joyride's STEP_AFTER
  // callback calls `history.push(expectedRoute)` which strips the query
  // string — without persistence the second step would see manualTrigger
  // = false, eligible would flip to false, and the tour would unmount.
  const [manualTrigger, setManualTrigger] = useState(() => {
    if (hasManualQueryParam()) return true;
    try {
      return sessionStorage.getItem(STORAGE_MANUAL_KEY) === "true";
    } catch (e) {
      return false;
    }
  });
  const debugMode = isDebugMode();

  const eligible = useMemo(() => {
    if (authLoading || !user) {
      if (debugMode) console.log("[Tour] blocked: auth not ready");
      return false;
    }
    // GoDeal is hard-excluded from the tour, ALWAYS — even with ?startTour=1.
    // The tour copy is wrong for travel-specific GoDeal flows and Raviv
    // explicitly asked us to never show it for companyId=1.
    if (String(user.companyId || "") === GODEAL_COMPANY_ID) {
      if (debugMode) console.log("[Tour] blocked: GoDeal companyId=1");
      return false;
    }
    // Manual trigger via ?startTour=1 — bypasses localStorage and freshness
    // checks. Used by the "Restart tour" link in profile menu and by
    // support to re-show the tour for a customer.
    if (manualTrigger) {
      if (debugMode) console.log("[Tour] eligible: manual ?startTour=1");
      return true;
    }
    if (localCompleted) {
      if (debugMode)
        console.log("[Tour] blocked: localStorage marks completed");
      return false;
    }
    if (!serverChecked) {
      if (debugMode) console.log("[Tour] waiting on /portal/tour/state");
      return false;
    }
    // Show the tour for every non-GoDeal tenant until they explicitly
    // finish or skip it. Previously we also gated on isFreshTenant (14d
    // window from user/company createdAt), but the createdAt was missing
    // for tenants provisioned before 2026-04-29 and the tour never
    // appeared. The localStorage + backend Setting flags below are the
    // single source of truth for "already shown / done".
    if (debugMode) console.log("[Tour] eligible — starting");
    return true;
  }, [
    authLoading,
    user,
    localCompleted,
    serverChecked,
    manualTrigger,
    debugMode,
  ]);

  // Track viewport so buildSteps can pick mobile-friendly variants when
  // the sidebar collapses behind the hamburger drawer. Re-runs on resize
  // / orientation change so a user rotating their phone gets a sane tour.
  const [viewportTick, setViewportTick] = useState(0);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    // Only track the viewport while the tour can actually show. Attaching
    // this unconditionally re-ran buildSteps (+ i18n calls) on EVERY resize
    // for users who finished the tour long ago — and iOS Safari fires resize
    // on every URL-bar collapse during normal scrolling.
    if (!eligible || tourClosed) return undefined;
    let raf = null;
    const onResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setViewportTick((n) => n + 1));
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [eligible, tourClosed]);

  const steps = useMemo(
    () => buildSteps(i18n.t.bind(i18n)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [viewportTick]
  );

  // Manual trigger via ?startTour=1 wipes the "completed" flags so the tour
  // can re-open. We do this here (not inside `eligible`) because eligible
  // is a pure derivation — it shouldn't have side-effects.
  useEffect(() => {
    if (!manualTrigger) return;
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_STEP_KEY);
    setStepIndex(0);
    setServerChecked(true);
    // Re-arm the kill-switch so a manual restart can re-open the tour
    // after a previous Finish/Skip on the same component instance.
    setTourClosed(false);
    try {
      sessionStorage.setItem(STORAGE_MANUAL_KEY, "true");
      if (debugMode) sessionStorage.setItem("whapiz-tour-debug", "true");
    } catch (e) {
      /* ignore quota / privacy mode errors */
    }
    // Strip ?startTour=1 from the URL so a refresh while the tour is
    // running doesn't loop us back to the welcome step. The sessionStorage
    // flag keeps the tour alive across page navigations.
    if (hasManualQueryParam() && typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("startTour");
      url.searchParams.delete("tourDebug");
      window.history.replaceState({}, "", url.toString());
    }
  }, [manualTrigger, debugMode]);

  // Sync with backend once on mount. If the backend says completed, mirror
  // it into localStorage so we don't ask again on subsequent loads.
  useEffect(() => {
    if (authLoading || !user) return;
    if (manualTrigger) return; // already handled above
    if (String(user.companyId || "") === GODEAL_COMPANY_ID) {
      setServerChecked(true);
      return;
    }
    if (localCompleted) {
      setServerChecked(true);
      return;
    }
    let cancelled = false;
    api
      .get("/portal/tour/state")
      .then((res) => {
        if (cancelled) return;
        if (res.data?.completed) {
          localStorage.setItem(STORAGE_KEY, "true");
        }
        setServerChecked(true);
      })
      .catch(() => {
        // If the endpoint isn't deployed yet, fall back to localStorage-only.
        if (!cancelled) setServerChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [authLoading, user, localCompleted, manualTrigger]);

  useEffect(() => {
    if (!eligible) return;
    // Auto-start the tour on the next frame so the layout has time to mount
    // (joyride needs target elements to exist when it starts).
    const t = setTimeout(() => setRun(true), 600);
    return () => clearTimeout(t);
  }, [eligible]);

  const finishTour = useCallback((completed) => {
    // Hard kill-switch FIRST — this is what guarantees the tooltip
    // unmounts on the next render no matter what joyride does next.
    setTourClosed(true);
    setRun(false);
    setStepIndex(0);
    // Always clear the manual-trigger flag so a refresh after finish/skip
    // doesn't re-open the tour. setManualTrigger flips eligibility to the
    // normal "completed" path, which respects localStorage.
    try {
      sessionStorage.removeItem(STORAGE_MANUAL_KEY);
      sessionStorage.removeItem("whapiz-tour-debug");
    } catch (e) {
      /* ignore */
    }
    setManualTrigger(false);
    if (completed) {
      // Recompute the per-tenant key INSIDE the callback so we don't
      // rely on closure-captured STORAGE_KEY from the first render
      // (which can resolve to `:anon` if AuthContext hadn't loaded
      // user yet). Reading it here uses the freshest companyId.
      const completedKey = tourKey(tenantCompanyId);
      const stepStorageKey = stepKey(tenantCompanyId);
      localStorage.setItem(completedKey, "true");
      localStorage.removeItem(stepStorageKey);
      // Best-effort backend sync. We deliberately don't await it — the user
      // shouldn't be blocked, and on retry the next page load's GET state
      // will find the localStorage flag and skip the tour anyway.
      api.post("/portal/tour/complete").catch(() => {
        // Network failure here is non-fatal; localStorage already prevents
        // the tour from re-popping on this device.
      });
    }
  }, [tenantCompanyId]);

  const handleCallback = useCallback(
    (data) => {
      const { action, index, status, type } = data;
      const lastIndex = steps.length - 1;

      if (debugMode) {
        // eslint-disable-next-line no-console
        console.log("[Tour] callback", { type, action, index, status });
      }

      // ── HARD FINISH GUARDS — checked BEFORE the navigation logic so
      // joyride's own per-version quirks can't keep the tooltip alive.
      // joyride v2.9 emits the Finish click as STEP_AFTER + NEXT on
      // the last index, and only then sends a separate TOUR_END with
      // status=finished. We finish on either signal.
      if (
        action === ACTIONS.SKIP ||
        status === STATUS.FINISHED ||
        status === STATUS.SKIPPED
      ) {
        if (debugMode)
          console.log("[Tour] finishing (status/skip)", { action, status });
        finishTour(true);
        return;
      }
      if (
        type === EVENTS.STEP_AFTER &&
        action === ACTIONS.NEXT &&
        index >= lastIndex
      ) {
        if (debugMode) console.log("[Tour] finishing — last step Next click");
        finishTour(true);
        return;
      }
      if (action === ACTIONS.CLOSE) {
        // X button. With disableOverlayClose=true this can't fire from
        // an overlay click, so treat any CLOSE as 'user wants out'.
        if (debugMode) console.log("[Tour] finishing — X / Close");
        finishTour(true);
        return;
      }

      // Persist current step so a page refresh resumes from where we left.
      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        const nextIndex = action === ACTIONS.PREV ? index - 1 : index + 1;
        const safeIndex = Math.max(0, Math.min(nextIndex, lastIndex));
        setStepIndex(safeIndex);
        // Per-tenant step key recomputed from live companyId — avoids
        // writing progress under the stale `:anon` suffix when the
        // AuthContext finished loading after this callback was first
        // memoized.
        localStorage.setItem(stepKey(tenantCompanyId), String(safeIndex));

        // Navigate to the route this step expects, but only if we're not
        // already there (avoid infinite re-renders).
        const expectedRoute = STEP_ROUTES[safeIndex];
        if (expectedRoute && location.pathname !== expectedRoute) {
          // Pause the tour briefly so the page can render, then resume.
          setRun(false);
          history.push(expectedRoute);
          setTimeout(() => setRun(true), 400);
        }
      }
    },
    [finishTour, history, location.pathname, steps.length, debugMode, tenantCompanyId]
  );

  // Hard kill-switch — wins over everything else, including a stale
  // manualTrigger that hasn't flushed yet. Without this the tooltip
  // sometimes lingered after Finish because joyride re-emitted a
  // STEP_AFTER event during its own unmount cycle.
  if (tourClosed) return null;
  if (!eligible) return null;

  return (
    <Joyride
      steps={steps}
      stepIndex={stepIndex}
      run={run}
      continuous
      showSkipButton
      showProgress
      // Don't dismiss the tour when the user accidentally clicks the
      // dimmed background — they have to use Skip / X / Finish. This
      // was the actual reason the tour was dying on the queues step:
      // joyride's default treats overlay clicks as ACTIONS.CLOSE.
      disableOverlayClose
      disableCloseOnEsc
      // Allow the user to actually click the highlighted menu item if
      // they want to navigate manually instead of using Next.
      spotlightClicks
      disableScrolling={false}
      scrollToFirstStep
      callback={handleCallback}
      locale={{
        back: i18n.t("tour.buttons.back", "Back"),
        close: i18n.t("tour.buttons.close", "Close"),
        last: i18n.t("tour.buttons.last", "Finish"),
        next: i18n.t("tour.buttons.next", "Next"),
        skip: i18n.t("tour.buttons.skip", "Skip tour"),
      }}
      // Floater (the popper that holds the tooltip) MUST sit above the
      // dimming overlay, otherwise the overlay swallows clicks on the
      // Next/Back/Skip buttons (Idan's bug). joyride's default puts both
      // at options.zIndex so we bump the floater explicitly. Both must
      // also be above MUI's Drawer (1200) and Modal (1300) so the tour
      // stays usable while a drawer/modal is open behind it.
      floaterProps={{
        styles: {
          floater: { zIndex: 13001 },
          floaterWithAnimation: { zIndex: 13001 },
          wrapper: { zIndex: 13001 },
        },
      }}
      styles={{
        options: {
          // Match Whapiz brand — emerald primary
          primaryColor: "#10b981",
          zIndex: 13000,
          arrowColor: "#ffffff",
          backgroundColor: "#ffffff",
          textColor: "#131614",
          // Cap tooltip width so it never overflows the viewport on phones.
          width: Math.min(
            (typeof window !== "undefined" ? window.innerWidth : 380) - 32,
            380
          ),
        },
        tooltip: {
          // Phones get tighter padding and smaller text for breathing room.
          padding: typeof window !== "undefined" && window.innerWidth < 480
            ? "16px"
            : "20px",
          borderRadius: 12,
          boxShadow: "0 12px 32px rgba(15,23,42,0.18)",
          // Belt-and-braces: make sure nothing on top of the tooltip
          // (e.g. an MUI overlay rendered later in DOM order) swallows
          // pointer events for the Next/Back/Skip buttons.
          pointerEvents: "auto",
          position: "relative",
          zIndex: 1,
        },
        tooltipFooter: {
          pointerEvents: "auto",
        },
        buttonClose: {
          pointerEvents: "auto",
        },
        tooltipTitle: {
          fontSize: typeof window !== "undefined" && window.innerWidth < 480
            ? 15
            : 16,
          fontWeight: 700,
          marginBottom: 8,
          lineHeight: 1.3,
        },
        tooltipContent: {
          fontSize: typeof window !== "undefined" && window.innerWidth < 480
            ? 13
            : 14,
          lineHeight: 1.55,
          whiteSpace: "pre-line",
          padding: 0,
        },
        buttonNext: {
          borderRadius: 8,
          fontWeight: 600,
          padding: "8px 18px",
          pointerEvents: "auto",
        },
        buttonBack: {
          color: "#64748b",
          marginRight: 8,
          pointerEvents: "auto",
        },
        buttonSkip: {
          color: "#94a3b8",
          pointerEvents: "auto",
        },
        spotlight: {
          // Slightly brighter spotlight so the highlighted item pops
          // visibly even on small screens.
          borderRadius: 8,
        },
      }}
    />
  );
};

export default OnboardingTour;
